import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";

import { useAuth } from "context/AuthContext";
import { fetchDeployStatus, promoteBetaToProd, rollbackProduction } from "services/deployControl";

const allowedDeployHosts = ["gs.saferdock.com", "localhost", "localhost:3000"];
const formatText = (value) => value || "—";

function DeployControl() {
  const [hostChecked, setHostChecked] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const host = window.location.host;
      if (!allowedDeployHosts.includes(host)) {
        window.location.replace("https://gs.saferdock.com/deploy");
        return;
      }
    }
    setHostChecked(true);
  }, []);

  const { profile } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDeployStatus();
      setStatus(data);
    } catch (err) {
      setError(err?.message || "Erro ao carregar status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hostChecked) {
      loadStatus();
    }
  }, [hostChecked, loadStatus]);

  const handlePromote = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await promoteBetaToProd();
      setSuccess("Produção atualizada com sucesso.");
      setStatus((prev) => ({
        ...(prev || {}),
        production_image: result.production_image,
        previous_production_image: result.previous_production_image,
      }));
    } catch (err) {
      setError(err?.message || "Erro ao atualizar produção.");
    } finally {
      setActionLoading(false);
      loadStatus();
    }
  };

  const handleRollback = async () => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await rollbackProduction();
      setSuccess("Produção revertida para versão anterior/estável.");
      setStatus((prev) => ({
        ...(prev || {}),
        production_image: result.production_image,
      }));
    } catch (err) {
      setError(err?.message || "Erro ao voltar versão.");
    } finally {
      setActionLoading(false);
      loadStatus();
    }
  };

  const isSuperuser = profile?.is_superuser;

  if (!hostChecked) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <VuiBox p={3}>
                <Typography variant="h5" gutterBottom>
                  Deploy (Beta → Produção)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Veja o que está rodando em Beta (GREEN) e Produção (BLUE). Só superusuário pode clicar nos botões.
                </Typography>
              </VuiBox>
              <Divider />
              <VuiBox p={3}>
                {loading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2">Carregando...</Typography>
                  </Stack>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <VuiBox p={2} display="flex" flexDirection="column" gap={1}>
                          <Typography variant="subtitle2" color="secondary">
                            Beta (GREEN)
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                            {formatText(status?.beta_image)}
                          </Typography>
                        </VuiBox>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <VuiBox p={2} display="flex" flexDirection="column" gap={1}>
                          <Typography variant="subtitle2" color="secondary">
                            Produção (BLUE)
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                            {formatText(status?.production_image)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Última produção anterior: {formatText(status?.previous_production_image)}
                          </Typography>
                        </VuiBox>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                <VuiBox mt={3} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!isSuperuser || actionLoading || loading}
                    onClick={handlePromote}
                  >
                    {actionLoading ? "Aguarde..." : "Atualizar versão de Produção"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={!isSuperuser || actionLoading || loading}
                    onClick={handleRollback}
                  >
                    {actionLoading ? "Aguarde..." : "Voltar versão"}
                  </Button>
                  <Button variant="text" onClick={loadStatus} disabled={loading}>
                    Recarregar
                  </Button>
                </VuiBox>

                {!isSuperuser && (
                  <VuiBox mt={2}>
                    <Typography variant="body2" color="error">
                      Apenas superusuário pode trocar versões.
                    </Typography>
                  </VuiBox>
                )}

                {error && (
                  <VuiBox mt={2}>
                    <Typography variant="body2" color="error">
                      {error}
                    </Typography>
                  </VuiBox>
                )}

                {success && (
                  <VuiBox mt={2}>
                    <Typography variant="body2" color="success.main">
                      {success}
                    </Typography>
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <VuiBox p={3}>
                <Typography variant="h6" gutterBottom>
                  Logs de Deploy
                </Typography>
                {loading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2">Carregando...</Typography>
                  </Stack>
                ) : (
                  <VuiBox
                    component="ul"
                    sx={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      maxHeight: 320,
                      overflowY: "auto",
                    }}
                  >
                    {(status?.logs || []).map((log, index) => (
                      <VuiBox
                        component="li"
                        key={`${log.timestamp}-${index}`}
                        mb={1.5}
                        p={2}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "8px",
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          {formatText(log.timestamp)}
                        </Typography>
                        <Typography variant="body2">
                          {log.event} | {log.status} | {formatText(log.by)}
                        </Typography>
                        <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                          {log.from ? `de: ${log.from} -> para: ${log.to}` : log.detail || ""}
                        </Typography>
                      </VuiBox>
                    ))}
                    {(status?.logs || []).length === 0 && (
                      <Typography variant="body2" color="textSecondary">
                        Nada registrado ainda.
                      </Typography>
                    )}
                  </VuiBox>
                )}
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DeployControl;
